/** Typed access to the shared language registry.
 *
 *  ``packages/i18n-languages/languages.json`` is the one file that
 *  defines every UI, output and document language. Nothing in the SPA
 *  keeps its own language list: read it from here. */
import type {
  LanguageEntry,
  LanguageRegistry,
  PluralCategory,
} from "@repoworks/i18n-languages";
import data from "@repoworks/i18n-languages/languages.json";

export type { LanguageEntry, LanguageRegistry, PluralCategory };

export const registry = data as LanguageRegistry;

/** Every registry entry, in registry order. */
export const languages: readonly LanguageEntry[] = registry.languages;

const BY_CODE = new Map(languages.map((l) => [l.code, l]));

/** The registry entry for an exact code, or undefined. */
export function entry(code: string | null | undefined): LanguageEntry | undefined {
  return code ? BY_CODE.get(code) : undefined;
}

/** The core locales (en, sv, fi): the hard parity gate. */
export function coreCodes(): readonly string[] {
  return registry.core;
}

/** Languages selectable as the UI language (their bundle ships). */
export function uiLanguages(): LanguageEntry[] {
  return languages.filter((l) => l.ui);
}

export function uiCodes(): string[] {
  return uiLanguages().map((l) => l.code);
}

/** Languages selectable as the session output language. */
export function outputLanguages(): LanguageEntry[] {
  return languages.filter((l) => l.output);
}

export function outputCodes(): string[] {
  return outputLanguages().map((l) => l.code);
}

/** i18next ``fallbackLng``: each entry's own chain, then English. */
export function fallbackLngMap(): Record<string, string[]> {
  const map: Record<string, string[]> = { default: [registry.source] };
  for (const l of languages) {
    if (l.fallback.length > 0) map[l.code] = [...l.fallback, registry.source];
  }
  return map;
}

interface AliasTable {
  codes: Map<string, string>;
  exact: Map<string, string>;
  /** ``xx-*`` patterns as ``[prefix "xx-", code]``, longest first. */
  wild: Array<[string, string]>;
}

const ALIAS_TABLES = new WeakMap<readonly LanguageEntry[], AliasTable>();

function aliasTable(list: readonly LanguageEntry[]): AliasTable {
  const cached = ALIAS_TABLES.get(list);
  if (cached) return cached;
  const table: AliasTable = { codes: new Map(), exact: new Map(), wild: [] };
  for (const l of list) {
    table.codes.set(l.code.toLowerCase(), l.code);
    for (const pattern of l.aliases) {
      const low = pattern.toLowerCase();
      if (low.endsWith("-*")) table.wild.push([low.slice(0, -1), l.code]);
      else table.exact.set(low, l.code);
    }
  }
  table.wild.sort((a, b) => b[0].length - a[0].length);
  ALIAS_TABLES.set(list, table);
  return table;
}

/** Map a BCP-47 tag to a registry code, or null.
 *
 *  Case-insensitive. Tries the exact code, then the exact aliases, then
 *  the ``xx-*`` patterns longest-first; then drops the last subtag and
 *  tries again (``de-CH-1996`` → ``de-CH`` → ``de``). ``allow`` limits
 *  the result to those codes (for example the ui languages); a match
 *  outside it keeps looking. Mirrors ``resolve`` in
 *  apps/docgen/docgen/languages.py. */
export function resolveCode(
  tag: string | null | undefined,
  allow?: ReadonlySet<string> | readonly string[],
  list: readonly LanguageEntry[] = languages,
): string | null {
  if (!tag) return null;
  const allowed = allow ? new Set(allow) : null;
  const ok = (code: string | undefined): code is string =>
    code !== undefined && (allowed === null || allowed.has(code));
  const { codes, exact, wild } = aliasTable(list);
  let current = tag.trim().replace(/_/g, "-").toLowerCase();
  while (current) {
    const byCode = codes.get(current);
    if (ok(byCode)) return byCode;
    const byAlias = exact.get(current);
    if (ok(byAlias)) return byAlias;
    for (const [prefix, code] of wild) {
      if (current.startsWith(prefix) && ok(code)) return code;
    }
    const cut = current.lastIndexOf("-");
    if (cut < 0) break;
    current = current.slice(0, cut);
  }
  return null;
}

/** The primary language subtag of a BCP-47 tag, or null if invalid. */
function primaryLanguage(tag: string): string | null {
  try {
    return new Intl.Locale(tag).language;
  } catch {
    return null;
  }
}

/** The Intl tag to format in for UI language ``code``.
 *
 *  The first browser tag that resolves to ``code`` AND has the same
 *  primary language as the entry's ``intl`` default keeps the reader's
 *  region: an en-GB browser gets dd/mm/yyyy in the English UI, de-CH
 *  its own grouping. A tag that resolves to a sibling entry never
 *  counts: zh-TW is zh-Hant, so a zh-Hans UI keeps Simplified formats,
 *  and pt-PT never formats a pt-BR UI. The primary-language check stops
 *  an alias from leaking a different language's formats (nn-NO
 *  resolves to nb, but a Bokmål UI never formats as Nynorsk).
 *  Otherwise the entry's ``intl``.
 *
 *  Only the language, script and region of a browser tag are kept, and
 *  the entry's ``intlExtension`` (``-u-nu-latn-ca-gregory``) is appended
 *  unless the tag already carries a ``-u-`` extension: Latin digits and
 *  the Gregorian calendar everywhere, so a Thai UI shows 2026, not 2569.
 *  An unknown code formats as English. */
export function intlTag(
  code: string,
  navigatorLanguages: readonly string[],
  list: readonly LanguageEntry[] = languages,
): string {
  const found =
    list.find((l) => l.code === code) ??
    list.find((l) => l.code === resolveCode(code, undefined, list)) ??
    list.find((l) => l.code === registry.source);
  if (!found) return code;
  const primary = primaryLanguage(found.intl);
  let tag = found.intl;
  for (const candidate of navigatorLanguages) {
    // Unrestricted on purpose: with an allow list the resolver keeps
    // cutting subtags, so zh-TW (zh-Hant) would fall back to zh and
    // pass as zh-Hans.
    if (resolveCode(candidate, undefined, list) !== found.code) continue;
    let locale: Intl.Locale;
    try {
      locale = new Intl.Locale(candidate);
    } catch {
      continue;
    }
    if (locale.language !== primary) continue;
    tag = locale.baseName;
    break;
  }
  return tag.includes("-u-") ? tag : `${tag}${found.intlExtension}`;
}
